import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'

const ToastContext = createContext(null)

// Module-level bridge so non-React modules (axios interceptors, lib/api.js,
// inline fetch error paths) can trigger a toast. Registered/unregistered by
// ToastProvider on mount.
let _externalToast = null
export function notifyExternally(message, type = 'info', durationMs = 3000) {
  if (_externalToast) _externalToast(message, type, durationMs)
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const idRef = useRef(0)

  const toast = useCallback((message, type = 'info', durationMs = 3000) => {
    const id = ++idRef.current
    setToasts(prev => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id))
    }, durationMs)
  }, [])

  useEffect(() => {
    _externalToast = toast
    return () => { _externalToast = null }
  }, [toast])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastStack toasts={toasts} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>')
  return ctx
}

function ToastStack({ toasts }) {
  if (!toasts.length) return null
  return (
    <div className="toast-stack">
      {toasts.map(t => (
        <div key={t.id} className={`toast toast--${t.type}`}>
          {t.message}
        </div>
      ))}
    </div>
  )
}
